import { APP_INITIALIZER, ApplicationConfig, importProvidersFrom, provideZoneChangeDetection, InjectionToken, Injector } from '@angular/core';
import { provideHttpClient } from '@angular/common/http';
import { provideRouter } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { environment } from '../environments/environment';
import { DemoDbService } from './core/demo-db.service';
import { DemoStorageService } from './core/demo-storage.service';
import { IncidentesService } from './features/incidentes/incidentes.service';
import { ContratistasService } from './features/contratistas/contratistas.service';
import { AuditoriasInternasService } from './features/auditorias-internas/auditorias-internas.service';
import { ComiteSstService } from './features/comite-sst/comite-sst.service';
import { EmergenciasSimulacrosService } from './features/emergencias-simulacros/emergencias-simulacros.service';
import { GestionCambioService } from './features/gestion-cambio/gestion-cambio.service';
import { PoliticaLiderazgoService } from './features/politica-liderazgo/politica-liderazgo.service';
import { DocumentalService } from './features/documental/documental.service';
import { EppService } from './features/epp/epp.service';
import { ConfiguracionService } from './features/configuracion/configuracion.service';
import { CapacitacionService } from './features/capacitacion/capacitacion.service';
import { VigilanciaSaludService } from './features/vigilancia-salud/vigilancia-salud.service';
import { InspeccionesService } from './features/inspecciones/inspecciones.service';
import { PeligrosRiesgosService } from './features/peligros-riesgos/peligros-riesgos.service';
import { NotificationsService } from './core/notifications.service';
import {
  LucideAngularModule,
  User,
  Key,
  Shield,
  LogOut,
  Bell,
  LayoutDashboard,
  TriangleAlert,
  ClipboardList,
  ClipboardCheck,
  Stethoscope,
  GraduationCap,
  Folder,
  ChartBar,
  Settings,
  Plus,
  Search,
  Filter,
  Pencil,
  Trash2,
  Eye,
  X,
  Check,
  ChevronsLeft,
  ChevronLeft,
  ChevronRight,
  ChevronsRight,
  FileText,
  GitBranch,
  AlertTriangle,
  Briefcase,
  UsersRound,
  HardHat,
  Factory,
  Store,
  Building2,
  Hotel,
  UtensilsCrossed,
  Truck,
  School,
} from 'lucide-angular';

import { routes } from './app.routes';
import { DEMO_LOADABLE } from './core/demo-tokens';

/** Arranque rápido: en demo no bloqueamos con IndexedDB; la hidratación corre en segundo plano (DemoInitRunnerService). */
function appInitializer(injector: Injector): () => Promise<void> {
  return () => {
    const demoDb = injector.get(DemoDbService);
    if (!environment.demo) return demoDb.init();
    return Promise.resolve();
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideHttpClient(),
    provideRouter(routes),
    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.production || environment.demo,
      registrationStrategy: 'registerWhenStable:30000',
    }),
    provideCharts(withDefaultRegisterables()),
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializer,
      deps: [Injector],
      multi: true,
    },
    { provide: DEMO_LOADABLE, useFactory: (s: IncidentesService) => () => s.loadFromDemoStorage(), deps: [IncidentesService], multi: true },
    { provide: DEMO_LOADABLE, useFactory: (s: ContratistasService) => () => s.loadFromDemoStorage(), deps: [ContratistasService], multi: true },
    { provide: DEMO_LOADABLE, useFactory: (s: AuditoriasInternasService) => () => s.loadFromDemoStorage(), deps: [AuditoriasInternasService], multi: true },
    { provide: DEMO_LOADABLE, useFactory: (s: ComiteSstService) => () => s.loadFromDemoStorage(), deps: [ComiteSstService], multi: true },
    { provide: DEMO_LOADABLE, useFactory: (s: EmergenciasSimulacrosService) => () => s.loadFromDemoStorage(), deps: [EmergenciasSimulacrosService], multi: true },
    { provide: DEMO_LOADABLE, useFactory: (s: GestionCambioService) => () => s.loadFromDemoStorage(), deps: [GestionCambioService], multi: true },
    { provide: DEMO_LOADABLE, useFactory: (s: PoliticaLiderazgoService) => () => s.loadFromDemoStorage(), deps: [PoliticaLiderazgoService], multi: true },
    { provide: DEMO_LOADABLE, useFactory: (s: DocumentalService) => () => s.loadFromDemoStorage(), deps: [DocumentalService], multi: true },
    { provide: DEMO_LOADABLE, useFactory: (s: EppService) => () => s.loadFromDemoStorage(), deps: [EppService], multi: true },
    { provide: DEMO_LOADABLE, useFactory: (s: ConfiguracionService) => () => s.loadFromDemoStorage(), deps: [ConfiguracionService], multi: true },
    { provide: DEMO_LOADABLE, useFactory: (s: CapacitacionService) => () => s.loadFromDemoStorage(), deps: [CapacitacionService], multi: true },
    { provide: DEMO_LOADABLE, useFactory: (s: VigilanciaSaludService) => () => s.loadFromDemoStorage(), deps: [VigilanciaSaludService], multi: true },
    { provide: DEMO_LOADABLE, useFactory: (s: InspeccionesService) => () => s.loadFromDemoStorage(), deps: [InspeccionesService], multi: true },
    { provide: DEMO_LOADABLE, useFactory: (s: PeligrosRiesgosService) => () => s.loadFromDemoStorage(), deps: [PeligrosRiesgosService], multi: true },
    { provide: DEMO_LOADABLE, useFactory: (s: NotificationsService) => () => s.loadFromDemoStorage(), deps: [NotificationsService], multi: true },
    importProvidersFrom(
      LucideAngularModule.pick({
        User,
        Key,
        Shield,
        LogOut,
        Bell,
        LayoutDashboard,
        TriangleAlert,
        ClipboardList,
        ClipboardCheck,
        Stethoscope,
        GraduationCap,
        Folder,
        ChartBar,
        Settings,
        Plus,
        Search,
        Filter,
        Pencil,
        Trash2,
        Eye,
        X,
        Check,
        ChevronsLeft,
        ChevronLeft,
        ChevronRight,
        ChevronsRight,
        FileText,
        GitBranch,
        AlertTriangle,
        Briefcase,
        UsersRound,
        HardHat,
        Factory,
        Store,
        Building2,
        Hotel,
        UtensilsCrossed,
        Truck,
        School,
      })
    ),
  ],
};
